import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { refreshTimeRangePresets } from '@/shared/components/ui/filters/time-range-filter/time-range-filter.utils';
import {
  TActiveFilterScopeProps,
  TAllowedFilterValues,
  TFilterScope,
  TFilterScopeProviderProps,
} from '@/shared/features/filter-preferences/context/filter-scope-context.types';
import { useFilterPreferences } from '@/shared/features/filter-preferences/hooks/use-filter-preferences/use-filter-preferences';
import { useTableViewState } from '@/shared/features/filter-preferences/hooks/use-table-view-state/use-table-view-state';
import {
  hasPersistableParams,
  toPersistableQuery,
} from '@/shared/features/filter-preferences/hooks/use-table-view-state/use-table-view-state.utils';

// No scopeKey: the feature is off. Defaults resolve straight to the code
// default, the query is never gated, and no preference read happens.
const NO_UNSAVED_PARAMS: readonly string[] = [];
const NO_ALLOWED_VALUES: TAllowedFilterValues = {};

const INACTIVE_SCOPE: TFilterScope = {
  scopeKey: undefined,
  isReady: true,
  didRestore: false,
  resolveDefault: (_param, codeDefault) => codeDefault,
  hasStoredDefault: () => false,
  unsavedParams: NO_UNSAVED_PARAMS,
};

const FilterScopeContext = createContext<TFilterScope>(INACTIVE_SCOPE);

export const useFilterScope = () => useContext(FilterScopeContext);

// Split out so the preference read only happens when a scopeKey is present.
const ActiveFilterScope = ({
  scopeKey,
  unsavedParams,
  allowedValues,
  children,
}: TActiveFilterScopeProps) => {
  const { resolveDefault, hasStoredDefault, getStoredDefaults, isReady } =
    useFilterPreferences();
  const { readSnapshot, saveQuery } = useTableViewState(scopeKey);
  const [searchParams, setSearchParams] = useSearchParams();
  const [seeded, setSeeded] = useState(false);
  const [didRestore, setDidRestore] = useState(false);
  const seededRef = useRef(false);

  // '' is a cleared default, which is never out of range.
  const isAllowed = (param: string, value = '') => {
    const allowed = allowedValues[param];
    return (
      !allowed || !value || value.split(',').every((v) => allowed.includes(v))
    );
  };

  // One owner writes the initial URL -- racing setSearchParams would lose all but
  // the last -- and the query stays gated until it has, so it fires once.
  useEffect(() => {
    if (!isReady || seededRef.current) return;
    seededRef.current = true;

    // The remembered view already holds the seeded defaults plus later changes,
    // so it wins over them; a deep link wins over both.
    const snapshot = readSnapshot();
    const snapshotParams = new URLSearchParams(snapshot?.query);
    const snapshotFits = [...snapshotParams].every(([param, value]) =>
      isAllowed(param, value),
    );
    if (
      snapshot?.query &&
      snapshotFits &&
      !hasPersistableParams(searchParams)
    ) {
      setSearchParams(refreshTimeRangePresets(snapshotParams), {
        replace: true,
      });
      setDidRestore(true);
      setSeeded(true);
      return;
    }

    const stored = getStoredDefaults(scopeKey, isAllowed);
    const next = new URLSearchParams(searchParams);
    let changed = false;
    // A deep link may carry a value this resource does not have; its default applies instead.
    [...next].forEach(([param, value]) => {
      if (isAllowed(param, value)) return;
      next.delete(param);
      changed = true;
    });
    Object.entries(stored).forEach(([param, value]) => {
      // '' is an explicitly cleared default: it stops resolution but seeds nothing.
      if (value && !next.has(param)) {
        next.set(param, value);
        changed = true;
      }
    });
    if (changed) setSearchParams(next, { replace: true });

    setSeeded(true);
  }, [
    isReady,
    scopeKey,
    searchParams,
    setSearchParams,
    getStoredDefaults,
    readSnapshot,
    allowedValues,
  ]);

  // Remember every later change, so a reset writes the reset state back too.
  useEffect(() => {
    if (!seeded) return;
    saveQuery(toPersistableQuery(searchParams, unsavedParams));
  }, [seeded, searchParams, saveQuery, unsavedParams]);

  const scope: TFilterScope = {
    scopeKey,
    isReady: isReady && seeded,
    didRestore,
    resolveDefault: (param, codeDefault) =>
      resolveDefault(scopeKey, param, codeDefault, isAllowed),
    hasStoredDefault: (param) => hasStoredDefault(scopeKey, param, isAllowed),
    unsavedParams,
  };

  return <FilterScopeContext value={scope}>{children}</FilterScopeContext>;
};

export const FilterScopeProvider = ({
  scopeKey,
  unsavedParams = NO_UNSAVED_PARAMS,
  allowedValues = NO_ALLOWED_VALUES,
  children,
}: TFilterScopeProviderProps) => {
  if (!scopeKey) {
    return (
      <FilterScopeContext value={INACTIVE_SCOPE}>{children}</FilterScopeContext>
    );
  }

  return (
    <ActiveFilterScope
      scopeKey={scopeKey}
      unsavedParams={unsavedParams}
      allowedValues={allowedValues}
    >
      {children}
    </ActiveFilterScope>
  );
};
